import {
  Box,
  Typography,
  IconButton,
  Snackbar,
  Alert,
  Skeleton,
  Chip,
  Tooltip,
} from "@mui/material";
import { ContentCopy, Terminal, SettingsEthernet } from "@mui/icons-material";
import { useEffect, useState } from "react";
import axios from "axios";
import { generateTelemetryCurl } from "../generateTelemetryCurl";
import { generateMqttxCommand } from "../generateMqttxCommand";
import { useAuth } from "../../../auth/useAuth";

import { APP_API_BASE } from "../../../config/api";
const API_BASE =
  process.env.REACT_APP_API_BASE_URL?.replace(/\/$/, "") ||
  `${APP_API_BASE}`;

function useCopy() {
  const [copied, setCopied] = useState(null);

  const copy = (key, value) => {
    navigator.clipboard.writeText(value);
    setCopied(key);
    setTimeout(() => setCopied(null), 2000);
  };

  return { copied, copy };
}

function CommandBlock({ icon, title, command, copyKey, copied, onCopy }) {
  const isCopied = copied === copyKey;

  return (
    <Box
      sx={{
        borderRadius: "14px",
        border: "1.5px solid #F1F5F9",
        overflow: "hidden",
        background: "#FAFBFF",
      }}
    >
      <Box
        sx={{
          px: 2,
          py: 1.5,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          borderBottom: "1px solid #F1F5F9",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Box
            sx={{
              width: 30,
              height: 30,
              borderRadius: "8px",
              background: "linear-gradient(135deg,#1976d2,#42a5f5)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "#fff",
            }}
          >
            {icon}
          </Box>

          <Typography fontSize={13} fontWeight={700}>
            {title}
          </Typography>
        </Box>

        <Tooltip title={isCopied ? "Copied!" : "Copy"}>
          <IconButton
            size="small"
            onClick={() => onCopy(copyKey, command)}
            sx={{
              background: isCopied ? "#DCFCE7" : "#EFF6FF",
              border: "1px solid",
              borderColor: isCopied ? "#BBF7D0" : "#BFDBFE",
            }}
          >
            <ContentCopy
              sx={{
                fontSize: 15,
                color: isCopied ? "#16a34a" : "#1976d2",
              }}
            />
          </IconButton>
        </Tooltip>
      </Box>

      <Box
        component="pre"
        sx={{
          m: 0,
          p: 2,
          background: "#0f172a",
          color: "#e5e7eb",
          fontSize: "13px",
          overflowX: "auto",
          fontFamily: "monospace",
        }}
      >
        {command}
      </Box>
    </Box>
  );
}

export default function CurlMqttCard({ deviceId, credentialRefreshKey = 0 }) {
  const [data, setData] = useState(null);
  const [schema, setSchema] = useState([]);
  const [loading, setLoading] = useState(true);

  const [snackbar, setSnackbar] = useState({ open: false, msg: "" });

  const { copied, copy } = useCopy();
  const { activeOrg } = useAuth();

  useEffect(() => {
    const token = localStorage.getItem("authToken");
    if (!token || !deviceId) return;

    setLoading(true);

    axios
      .get(`${API_BASE}/api/v1/devices/${deviceId}/connection`, {
        headers: {
          Authorization: `Bearer ${token}`,
          ...(activeOrg?.id && { "X-Org-ID": activeOrg.id }),
        },
      })
      .then((res) => setData(res.data))
      .finally(() => setLoading(false));
  }, [deviceId, activeOrg?.id, credentialRefreshKey]);

  useEffect(() => {
    const token = localStorage.getItem("authToken");
    if (!token || !deviceId) return;

    axios
      .get(`${API_BASE}/api/v1/devices/${deviceId}`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((res) => setSchema(res.data.schema));
  }, [deviceId, credentialRefreshKey]);

  if (!data && !loading) return null;

  const curlCommand = generateTelemetryCurl({
    apiBase: API_BASE,
    deviceId: data?.device_id,
    token: localStorage.getItem("authToken"),
    schema,
    metrics: data?.metric_catalog || [],
  });

  const mqttxCommand = generateMqttxCommand({
    broker: data?.broker,
    port: data?.port,
    deviceId: data?.device_id,
    authToken: data?.password,
    username: data?.username,
    topic: data?.telemetry_topic,
    schema,
    securityMode: data?.security_mode,
    caCertFilename: data?.ca_cert_filename,
    clientCertFilename: data?.client_cert_filename,
    clientKeyFilename: data?.client_key_filename,
    metrics: data?.metric_catalog || [],
  });

  const handleCopy = (key, value) => {
    copy(key, value);
    setSnackbar({ open: true, msg: "Command copied to clipboard" });
  };

  return (
    <>
      <Box
        sx={{
          borderRadius: "18px",
          overflow: "hidden",
          border: "1.5px solid #E2E8F0",
          background: "#fff",
          boxShadow: "0 4px 24px rgba(0,0,0,0.06)",
        }}
      >
        <Box
          sx={{
            px: 3,
            py: 2,
            background: "linear-gradient(135deg,#EFF6FF,#F0F9FF)",
            borderBottom: "1.5px solid #E2E8F0",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Box sx={{ display: "flex", gap: 1.5 }}>
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: "12px",
                background: "linear-gradient(135deg,#1976d2,#42a5f5)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "#fff",
              }}
            >
              <Terminal />
            </Box>

            <Box>
              <Typography fontWeight={700} fontSize={15}>
                Device Commands
              </Typography>
              <Typography fontSize={11} color="#64748B">
                Test device using HTTP or MQTTX
              </Typography>
            </Box>
          </Box>

          <Chip
            label={
              String(data?.security_mode || "PLAINTEXT").toUpperCase() === "TLS"
                ? "TLS Profile"
                : "Plain MQTT"
            }
            size="small"
            sx={{
              background: "#E0F2FE",
              color: "#0369A1",
              fontWeight: 600,
              fontSize: "11px",
            }}
          />
        </Box>

        <Box sx={{ p: 2, display: "flex", flexDirection: "column", gap: 2 }}>
          {loading ? (
            <>
              <Skeleton height={120} />
              <Skeleton height={120} />
            </>
          ) : (
            <>
              <CommandBlock
                icon={<Terminal sx={{ fontSize: 16 }} />}
                title="Curl Telemetry Command"
                command={curlCommand}
                copyKey="curl"
                copied={copied}
                onCopy={handleCopy}
              />

              <CommandBlock
                icon={<SettingsEthernet sx={{ fontSize: 16 }} />}
                title="MQTTX Publish Command"
                command={mqttxCommand}
                copyKey="mqtt"
                copied={copied}
                onCopy={handleCopy}
              />
            </>
          )}
        </Box>
      </Box>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={2500}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          severity="success"
          variant="filled"
          onClose={() => setSnackbar({ ...snackbar, open: false })}
        >
          {snackbar.msg}
        </Alert>
      </Snackbar>
    </>
  );
}
